import React, { useState, useEffect } from 'react';
import AppShell from '../components/layout/AppShell';
import { PageHeader } from '../components/layout/SharedComponents';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import api from '../services/api';
import { toast } from 'sonner';
import { Shield, Key, Info, AlertTriangle } from 'lucide-react';

const AUTH_METHODS = [
  { value: 'email_password', label: 'Email & Password (Default)', icon: Key },
  { value: 'google_oauth', label: 'Google Workspace / OAuth', icon: Shield },
  { value: 'microsoft_entra', label: 'Microsoft Entra ID (Azure AD)', icon: Shield },
  { value: 'okta_saml', label: 'Okta SAML', icon: Shield },
  { value: 'onelogin', label: 'OneLogin SAML', icon: Shield },
  { value: 'auth0', label: 'Auth0', icon: Shield },
  { value: 'ping_identity', label: 'Ping Identity / PingFederate', icon: Shield },
  { value: 'ldap', label: 'LDAP / Active Directory', icon: Shield },
  { value: 'jumpcloud', label: 'JumpCloud', icon: Shield },
  { value: 'custom_saml', label: 'Custom SAML Provider', icon: Shield },
];

export default function AuthSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [authMethod, setAuthMethod] = useState('email_password');
  const [authConfig, setAuthConfig] = useState({});
  const [originalMethod, setOriginalMethod] = useState('email_password');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/system/auth-settings');
      setAuthMethod(res.data.auth_method || 'email_password');
      setAuthConfig(res.data.auth_config || {});
      setOriginalMethod(res.data.auth_method || 'email_password');
    } catch (err) {
      if (err?.response?.status >= 500) toast.error('Failed to load authentication settings');
    }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/api/system/auth-settings', {
        auth_method: authMethod,
        auth_config: authConfig
      });
      toast.success('Authentication settings updated successfully');
      setOriginalMethod(authMethod);
      
      if (authMethod !== originalMethod) {
        toast.info('Users will see the new login method on their next login attempt');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    }
    setSaving(false);
  };

  const updateConfig = (key, value) => {
    setAuthConfig(prev => ({ ...prev, [key]: value }));
  };

  const renderConfigFields = () => {
    switch (authMethod) {
      case 'email_password':
        return (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              Email & Password is the default authentication method. No additional configuration required.
              Users will log in with their email address and password.
            </AlertDescription>
          </Alert>
        );

      case 'google_oauth':
        return (
          <div className="space-y-4">
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                To use Google OAuth, you need to create a project in Google Cloud Console and obtain OAuth credentials.
                <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="ml-1 text-primary hover:underline">
                  Get credentials →
                </a>
              </AlertDescription>
            </Alert>
            
            <div>
              <Label htmlFor="google_client_id">Google Client ID *</Label>
              <Input
                id="google_client_id"
                value={authConfig.google_client_id || ''}
                onChange={(e) => updateConfig('google_client_id', e.target.value)}
                placeholder="123456789-abc.apps.googleusercontent.com"
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground mt-1">
                The OAuth 2.0 Client ID from your Google Cloud Console
              </p>
            </div>

            <div>
              <Label htmlFor="allowed_domains">Allowed Email Domains (Optional)</Label>
              <Input
                id="allowed_domains"
                value={authConfig.allowed_domains || ''}
                onChange={(e) => updateConfig('allowed_domains', e.target.value)}
                placeholder="yourcompany.com, subsidiary.com"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Comma-separated list of domains. Leave empty to allow all domains.
              </p>
            </div>
          </div>
        );

      case 'okta_saml':
        return (
          <div className="space-y-4">
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Configure Okta SAML integration. You'll need your Okta domain and application credentials.
              </AlertDescription>
            </Alert>

            <div>
              <Label htmlFor="okta_domain">Okta Domain *</Label>
              <Input
                id="okta_domain"
                value={authConfig.okta_domain || ''}
                onChange={(e) => updateConfig('okta_domain', e.target.value)}
                placeholder="yourcompany.okta.com"
              />
            </div>

            <div>
              <Label htmlFor="okta_client_id">Okta Client ID *</Label>
              <Input
                id="okta_client_id"
                value={authConfig.okta_client_id || ''}
                onChange={(e) => updateConfig('okta_client_id', e.target.value)}
                placeholder="0oa..."
                className="font-mono text-sm"
              />
            </div>

            <div>
              <Label htmlFor="okta_client_secret">Okta Client Secret *</Label>
              <Input
                id="okta_client_secret"
                type="password"
                value={authConfig.okta_client_secret || ''}
                onChange={(e) => updateConfig('okta_client_secret', e.target.value)}
                placeholder="Enter client secret"
                className="font-mono text-sm"
              />
            </div>
          </div>
        );

      case 'microsoft_entra':
        return (
          <div className="space-y-4">
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Configure Microsoft Entra ID (formerly Azure AD). Register an application in Azure Portal.
                <a href="https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps" target="_blank" rel="noopener noreferrer" className="ml-1 text-primary hover:underline">
                  Azure Portal →
                </a>
              </AlertDescription>
            </Alert>

            <div>
              <Label htmlFor="entra_tenant_id">Entra Tenant ID *</Label>
              <Input
                id="entra_tenant_id"
                value={authConfig.entra_tenant_id || ''}
                onChange={(e) => updateConfig('entra_tenant_id', e.target.value)}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Your Azure AD Directory (tenant) ID
              </p>
            </div>

            <div>
              <Label htmlFor="entra_client_id">Application (Client) ID *</Label>
              <Input
                id="entra_client_id"
                value={authConfig.entra_client_id || ''}
                onChange={(e) => updateConfig('entra_client_id', e.target.value)}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                className="font-mono text-sm"
              />
            </div>

            <div>
              <Label htmlFor="entra_client_secret">Client Secret *</Label>
              <Input
                id="entra_client_secret"
                type="password"
                value={authConfig.entra_client_secret || ''}
                onChange={(e) => updateConfig('entra_client_secret', e.target.value)}
                placeholder="Enter client secret"
                className="font-mono text-sm"
              />
            </div>
          </div>
        );

      case 'onelogin':
        return (
          <div className="space-y-4">
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Configure OneLogin SAML integration.
                <a href="https://admin.onelogin.com" target="_blank" rel="noopener noreferrer" className="ml-1 text-primary hover:underline">
                  OneLogin Admin →
                </a>
              </AlertDescription>
            </Alert>

            <div>
              <Label htmlFor="onelogin_subdomain">OneLogin Subdomain *</Label>
              <Input
                id="onelogin_subdomain"
                value={authConfig.onelogin_subdomain || ''}
                onChange={(e) => updateConfig('onelogin_subdomain', e.target.value)}
                placeholder="yourcompany"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Your OneLogin subdomain (e.g., yourcompany.onelogin.com)
              </p>
            </div>

            <div>
              <Label htmlFor="onelogin_client_id">Client ID *</Label>
              <Input
                id="onelogin_client_id"
                value={authConfig.onelogin_client_id || ''}
                onChange={(e) => updateConfig('onelogin_client_id', e.target.value)}
                className="font-mono text-sm"
              />
            </div>

            <div>
              <Label htmlFor="onelogin_client_secret">Client Secret *</Label>
              <Input
                id="onelogin_client_secret"
                type="password"
                value={authConfig.onelogin_client_secret || ''}
                onChange={(e) => updateConfig('onelogin_client_secret', e.target.value)}
                className="font-mono text-sm"
              />
            </div>
          </div>
        );

      case 'auth0':
        return (
          <div className="space-y-4">
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Configure Auth0 authentication.
                <a href="https://manage.auth0.com" target="_blank" rel="noopener noreferrer" className="ml-1 text-primary hover:underline">
                  Auth0 Dashboard →
                </a>
              </AlertDescription>
            </Alert>

            <div>
              <Label htmlFor="auth0_domain">Auth0 Domain *</Label>
              <Input
                id="auth0_domain"
                value={authConfig.auth0_domain || ''}
                onChange={(e) => updateConfig('auth0_domain', e.target.value)}
                placeholder="yourcompany.auth0.com or yourcompany.us.auth0.com"
              />
            </div>

            <div>
              <Label htmlFor="auth0_client_id">Client ID *</Label>
              <Input
                id="auth0_client_id"
                value={authConfig.auth0_client_id || ''}
                onChange={(e) => updateConfig('auth0_client_id', e.target.value)}
                className="font-mono text-sm"
              />
            </div>

            <div>
              <Label htmlFor="auth0_client_secret">Client Secret *</Label>
              <Input
                id="auth0_client_secret"
                type="password"
                value={authConfig.auth0_client_secret || ''}
                onChange={(e) => updateConfig('auth0_client_secret', e.target.value)}
                className="font-mono text-sm"
              />
            </div>
          </div>
        );

      case 'ping_identity':
        return (
          <div className="space-y-4">
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Configure Ping Identity / PingFederate SAML integration.
              </AlertDescription>
            </Alert>

            <div>
              <Label htmlFor="ping_base_url">PingFederate Base URL *</Label>
              <Input
                id="ping_base_url"
                value={authConfig.ping_base_url || ''}
                onChange={(e) => updateConfig('ping_base_url', e.target.value)}
                placeholder="https://sso.yourcompany.com"
              />
            </div>

            <div>
              <Label htmlFor="ping_entity_id">Entity ID *</Label>
              <Input
                id="ping_entity_id"
                value={authConfig.ping_entity_id || ''}
                onChange={(e) => updateConfig('ping_entity_id', e.target.value)}
                placeholder="urn:yourcompany:pingfederate"
              />
            </div>
          </div>
        );

      case 'ldap':
        return (
          <div className="space-y-4">
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Configure LDAP or Active Directory authentication.
              </AlertDescription>
            </Alert>

            <div>
              <Label htmlFor="ldap_server">LDAP Server *</Label>
              <Input
                id="ldap_server"
                value={authConfig.ldap_server || ''}
                onChange={(e) => updateConfig('ldap_server', e.target.value)}
                placeholder="ldap://ldap.yourcompany.com or ldaps://ldap.yourcompany.com"
              />
            </div>

            <div>
              <Label htmlFor="ldap_base_dn">Base DN *</Label>
              <Input
                id="ldap_base_dn"
                value={authConfig.ldap_base_dn || ''}
                onChange={(e) => updateConfig('ldap_base_dn', e.target.value)}
                placeholder="dc=yourcompany,dc=com"
              />
            </div>

            <div>
              <Label htmlFor="ldap_bind_dn">Bind DN (Optional)</Label>
              <Input
                id="ldap_bind_dn"
                value={authConfig.ldap_bind_dn || ''}
                onChange={(e) => updateConfig('ldap_bind_dn', e.target.value)}
                placeholder="cn=admin,dc=yourcompany,dc=com"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Leave empty for anonymous bind
              </p>
            </div>

            <div>
              <Label htmlFor="ldap_bind_password">Bind Password (Optional)</Label>
              <Input
                id="ldap_bind_password"
                type="password"
                value={authConfig.ldap_bind_password || ''}
                onChange={(e) => updateConfig('ldap_bind_password', e.target.value)}
              />
            </div>
          </div>
        );

      case 'jumpcloud':
        return (
          <div className="space-y-4">
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Configure JumpCloud SAML integration.
                <a href="https://console.jumpcloud.com" target="_blank" rel="noopener noreferrer" className="ml-1 text-primary hover:underline">
                  JumpCloud Console →
                </a>
              </AlertDescription>
            </Alert>

            <div>
              <Label htmlFor="jumpcloud_org_id">Organization ID *</Label>
              <Input
                id="jumpcloud_org_id"
                value={authConfig.jumpcloud_org_id || ''}
                onChange={(e) => updateConfig('jumpcloud_org_id', e.target.value)}
                className="font-mono text-sm"
              />
            </div>

            <div>
              <Label htmlFor="jumpcloud_app_id">Application ID *</Label>
              <Input
                id="jumpcloud_app_id"
                value={authConfig.jumpcloud_app_id || ''}
                onChange={(e) => updateConfig('jumpcloud_app_id', e.target.value)}
                className="font-mono text-sm"
              />
            </div>

            <div>
              <Label htmlFor="jumpcloud_api_key">API Key *</Label>
              <Input
                id="jumpcloud_api_key"
                type="password"
                value={authConfig.jumpcloud_api_key || ''}
                onChange={(e) => updateConfig('jumpcloud_api_key', e.target.value)}
                className="font-mono text-sm"
              />
            </div>
          </div>
        );

      case 'entra_id':
        // Keep old name for backward compatibility
        return renderConfigFields.call(this, 'microsoft_entra');

      case 'custom_saml':
        return (
          <div className="space-y-4">
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Configure Microsoft Entra ID (formerly Azure AD). Register an application in Azure Portal.
                <a href="https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps" target="_blank" rel="noopener noreferrer" className="ml-1 text-primary hover:underline">
                  Azure Portal →
                </a>
              </AlertDescription>
            </Alert>

            <div>
              <Label htmlFor="entra_tenant_id">Entra Tenant ID *</Label>
              <Input
                id="entra_tenant_id"
                value={authConfig.entra_tenant_id || ''}
                onChange={(e) => updateConfig('entra_tenant_id', e.target.value)}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Your Azure AD Directory (tenant) ID
              </p>
            </div>

            <div>
              <Label htmlFor="entra_client_id">Application (Client) ID *</Label>
              <Input
                id="entra_client_id"
                value={authConfig.entra_client_id || ''}
                onChange={(e) => updateConfig('entra_client_id', e.target.value)}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                className="font-mono text-sm"
              />
            </div>

            <div>
              <Label htmlFor="entra_client_secret">Client Secret *</Label>
              <Input
                id="entra_client_secret"
                type="password"
                value={authConfig.entra_client_secret || ''}
                onChange={(e) => updateConfig('entra_client_secret', e.target.value)}
                placeholder="Enter client secret"
                className="font-mono text-sm"
              />
            </div>
          </div>
        );

      case 'custom_saml':
        return (
          <div className="space-y-4">
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Configure a custom SAML 2.0 identity provider.
              </AlertDescription>
            </Alert>

            <div>
              <Label htmlFor="saml_entity_id">SAML Entity ID *</Label>
              <Input
                id="saml_entity_id"
                value={authConfig.saml_entity_id || ''}
                onChange={(e) => updateConfig('saml_entity_id', e.target.value)}
                placeholder="https://idp.yourcompany.com/saml"
              />
            </div>

            <div>
              <Label htmlFor="saml_sso_url">SSO URL *</Label>
              <Input
                id="saml_sso_url"
                value={authConfig.saml_sso_url || ''}
                onChange={(e) => updateConfig('saml_sso_url', e.target.value)}
                placeholder="https://idp.yourcompany.com/sso"
              />
            </div>

            <div>
              <Label htmlFor="saml_certificate">X.509 Certificate *</Label>
              <textarea
                id="saml_certificate"
                value={authConfig.saml_certificate || ''}
                onChange={(e) => updateConfig('saml_certificate', e.target.value)}
                placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
                className="w-full min-h-[120px] px-3 py-2 rounded-md border border-input bg-background text-sm font-mono"
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <AppShell title="Authentication Settings">
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Loading settings...</div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell title="Authentication Settings">
      <PageHeader
        title="Authentication Settings"
        subtitle="Configure how users sign in to your organization"
      />

      <div className="max-w-3xl">
        <Alert className="mb-6" variant="warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>Important:</strong> Changing authentication method will affect all users.
            Ensure you've properly configured and tested the new method before switching.
            Email/password authentication will always remain available as a fallback.
          </AlertDescription>
        </Alert>

        <Card>
          <CardHeader>
            <CardTitle>Authentication Method</CardTitle>
            <CardDescription>
              Choose how users will authenticate when logging in
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label htmlFor="auth_method">Authentication Method</Label>
              <Select value={authMethod} onValueChange={setAuthMethod}>
                <SelectTrigger id="auth_method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AUTH_METHODS.map(method => (
                    <SelectItem key={method.value} value={method.value}>
                      {method.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Current method: <strong>{AUTH_METHODS.find(m => m.value === originalMethod)?.label}</strong>
              </p>
            </div>

            {renderConfigFields()}

            <div className="flex gap-2 pt-4 border-t">
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
              <Button variant="outline" onClick={loadSettings} disabled={saving}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-base">Testing & Support</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <p className="text-muted-foreground">
              After configuring SSO, we recommend testing in an incognito window before rolling out to all users.
            </p>
            <p className="text-muted-foreground">
              Need help? Contact <a href="mailto:support@eyvo.com" className="text-primary hover:underline">support@eyvo.com</a> for assistance with SSO configuration.
            </p>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}
